import { Inject, Injectable } from '@angular/core';

import { Observable, combineLatest, map, share } from 'rxjs';

import { flagEnumContainsValue } from '../../../../../../base/utils/flag-enum-contains-value-utils';
import { StakeUpdateErrorModel } from '../../../../models';
import { PlaceButtonStates } from '../../../../state';
import { TotalStake } from '../../../stake/stake-utils';
import { isUpdateStakeState } from '../../place-button-state.utils';
import {
    IPlaceButtonActionHandlerService,
    IPlaceButtonActionService,
    IPlaceButtonDisableService,
    IPlaceButtonStateService,
    PLACE_BUTTON_ACTION_HANDLER_SERVICE,
    PLACE_BUTTON_DISABLE_SERVICE,
    PLACE_BUTTON_STATE_SERVICE,
    PlaceButtonActionPredicate,
} from '../interfaces/place-button-interfaces';

@Injectable()
export class BetslipPlaceButtonActionService implements IPlaceButtonActionService {
    action$: Observable<PlaceButtonActionPredicate>;

    constructor(
        @Inject(PLACE_BUTTON_STATE_SERVICE) placeButtonService: IPlaceButtonStateService,
        @Inject(PLACE_BUTTON_DISABLE_SERVICE) disableButtonService: IPlaceButtonDisableService,
        @Inject(PLACE_BUTTON_ACTION_HANDLER_SERVICE) private actionHandler: IPlaceButtonActionHandlerService,
    ) {
        const placeButtonState$ = placeButtonService.state$;
        const isDisabled$ = disableButtonService.isButtonDisabled$;

        this.action$ = combineLatest([placeButtonState$, isDisabled$]).pipe(
            map(([state, isDisabled]) => this.getActionHandler(state.action, state.stakes, isDisabled, state.isFreeBet, state.stakeUpdateErrors)),
            share(),
        );
    }

    private getActionHandler(
        action: PlaceButtonStates,
        stakes: TotalStake[],
        isDisabled: boolean,
        isFreeBet: boolean,
        betSlipMinStakeErrors: StakeUpdateErrorModel[],
    ): PlaceButtonActionPredicate {
        if (isDisabled) {
            return () => false;
        }

        if (isUpdateStakeState(action)) {
            return this.actionHandler.getUpdateMinStakeHandler(betSlipMinStakeErrors);
        }

        if (flagEnumContainsValue(action, PlaceButtonStates.Deposit)) {
            if (flagEnumContainsValue(action, PlaceButtonStates.Processing)) {
                return this.actionHandler.makeDepositHandler(stakes);
            }
            if (flagEnumContainsValue(action, PlaceButtonStates.PlaceBet)) {
                return this.actionHandler.getDepositAndBetHandler();
            }

            return this.actionHandler.getDepositHandler();
        }

        if (flagEnumContainsValue(action, PlaceButtonStates.PlaceBet)) {
            if (flagEnumContainsValue(action, PlaceButtonStates.AcceptChanges)) {
                return this.actionHandler.makeAcceptAndPlaceBetHandler(isFreeBet);
            }

            return this.actionHandler.makePlaceHandler(isFreeBet);
        }

        if (flagEnumContainsValue(action, PlaceButtonStates.Confirm)) {
            return this.actionHandler.getConfirmHandler();
        }

        if (flagEnumContainsValue(action, PlaceButtonStates.AcceptChanges)) {
            return this.actionHandler.getAcceptChangesHandler();
        }

        if (flagEnumContainsValue(action, PlaceButtonStates.Login)) {
            return this.actionHandler.getLoginHandler();
        }

        return () => {
            throw new Error(`Betslip button action error. Invalid action found: ${action}!`);
        };
    }
}
